
      import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import translations from "./i18n/translations";
import { toast, Toaster } from "sonner";
import RouteMap from "./RouteMap";
import Reviews from "./Reviews";
import RadioPlayer from "./RadioPlayer";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHATSAPP_NUMBER = "34673867999";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
// Alternative endpoint that reliably prefills the message on both WhatsApp and WhatsApp Business
const WHATSAPP_SEND_URL = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}`;

const LOGO_CLEAN = "https://customer-assets.emergentagent.com/job_bf3d52d1-4f5f-47e0-aaa8-bb73dc6635d2/artifacts/m948yyw5_LOGO%20IBIALCON.png";
const HERO_IMG = "https://customer-assets.emergentagent.com/job_bf3d52d1-4f5f-47e0-aaa8-bb73dc6635d2/artifacts/idxkv0zr_Gemini_Generated_Image_51r0ab51r0ab51r0.png";
const VITO_EXT_NIGHT = "https://static.prod-images.emergentagent.com/jobs/bf3d52d1-4f5f-47e0-aaa8-bb73dc6635d2/images/5e012badcd71088b454173ac1e306e61d68ed404d2d5d4b5bad57268ce278f4b.png";
const VITO_FLEET_AIRPORT = "https://customer-assets.emergentagent.com/job_ibialcon-rides/artifacts/p1hcro5s_WhatsApp%20Image%202026-04-20%20at%2021.11.41%20%281%29.jpeg";
const VITO_INTERIOR_DUSK = "https://customer-assets.emergentagent.com/job_ibialcon-rides/artifacts/al323rhu_WhatsApp%20Image%202026-04-20%20at%2021.11.41.jpeg";
const VITO_HOTEL_NIGHT = "https://customer-assets.emergentagent.com/job_ibialcon-rides/artifacts/syoy1ewk_WhatsApp%20Image%202026-04-20%20at%2021.11.42%20%281%29.jpeg";
const ES_VEDRA = "https://images.unsplash.com/photo-1694287095846-ed7b44f07f37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxJYml6YSUyMEVzJTIwVmVkcmElMjBzdW5zZXR8ZW58MHx8fHwxNzc2NzExOTU0fDA&ixlib=rb-4.1.0&q=85";
const CINEMATIC_VIDEO = "https://assets.mixkit.co/videos/47701/47701-720.mp4";
const CINEMATIC_POSTER = "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1920&q=80";

const ZONES_FALLBACK = [
  { id: "1", price: 45 },
  { id: "2", price: 55 },
  { id: "3", price: 65 },
  { id: "4", price: 80 },
];

const DISPOSAL_FALLBACK = {
  "3h":  { price: 180, discount_pct: 0 },
  "6h":  { price: 300, discount_pct: 0 },
  "12h": { price: 540, discount_pct: 0 },
  "24h": { price: 900, discount_pct: 0 },
  extra_hour: 70,
};

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const IconChevron = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);
const IconWhatsapp = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);
const IconGlobe = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
  </svg>
);

export default function Landing() {
  const [lang, setLang] = useState("en");
  const t = translations[lang];
  const [scrolled, setScrolled] = useState(false);
  const [zones, setZones] = useState(ZONES_FALLBACK);
  const [disposal, setDisposal] = useState(DISPOSAL_FALLBACK);
  useScrollReveal();

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      if (Array.isArray(data?.zones) && data.zones.length) {
        setZones(data.zones.map((z) => ({ id: z.id, price: z.price })));
      }
      if (data?.disposal) setDisposal(data.disposal);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Booking state
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    time: "",
    origin: "",
    destination: "",
    zone: "",
    passengers: 2,
    notes: "",
    serviceType: "transfer",  // "transfer" | "disposal"
    disposalBlock: "3h",
    extraHours: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [lastBooking, setLastBooking] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);

  const selectedZone = useMemo(() => zones.find((z) => z.id === form.zone), [form.zone, zones]);

  // Compute disposal totals locally so the user sees price live.
  const disposalQuote = useMemo(() => {
    if (form.serviceType !== "disposal") return null;
    const blockCfg = disposal[form.disposalBlock] || {};
    const basePrice = Number(blockCfg.price || 0);
    const discountPct = Number(blockCfg.discount_pct || 0);
    const blockDiscounted = Math.round(basePrice * (1 - discountPct / 100));
    const extra = Math.max(0, Number(form.extraHours) || 0);
    const extraTotal = extra * Number(disposal.extra_hour || 0);
    const total = blockDiscounted + extraTotal;
    return {
      basePrice,
      discountPct,
      discountAmount: basePrice - blockDiscounted,
      blockDiscounted,
      extraTotal,
      total,
    };
  }, [form.serviceType, form.disposalBlock, form.extraHours, disposal]);

  const price = form.serviceType === "disposal"
    ? (disposalQuote ? disposalQuote.total : null)
    : (selectedZone ? selectedZone.price : null);

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const buildWhatsappMessage = (b) => {
    const isDisposal = b.serviceType === "disposal";
    const lines = lang === "es"
      ? [
          "¡Hola IBIALCON! Quiero confirmar mi reserva VIP:",
          "",
          `• Servicio: ${isDisposal ? "Disposición (vehículo por horas)" : "Transfer (punto a punto)"}`,
          `• Nombre: ${b.name}`,
          `• Teléfono: ${b.phone}`,
          b.email ? `• Email: ${b.email}` : null,
          `• Fecha: ${b.date}`,
          `• Hora: ${b.time}`,
          `• Origen: ${b.origin}`,
          `• Destino: ${b.destination}`,
          !isDisposal && b.distanceKm ? `• Distancia: ${b.distanceKm} km · ${b.durationMin} min aprox.` : null,
          !isDisposal ? `• Zona: ${b.zone} — ${b.price}€` : null,
          isDisposal ? `• Bloque: ${b.disposalBlock}${Number(b.extraHours) > 0 ? ` + ${b.extraHours}h extra` : ""}` : null,
          isDisposal ? `• Total: ${b.price}€` : null,
          `• Pasajeros: ${b.passengers}`,
          `• Vehículo: Mercedes Vito (8 plazas) · VIP`,
          b.notes ? `• Notas: ${b.notes}` : null,
          "",
          "¡Gracias!",
        ]
      : [
          "Hi IBIALCON! I'd like to confirm my VIP booking:",
          "",
          `• Service: ${isDisposal ? "Disposal (hourly vehicle)" : "Transfer (point-to-point)"}`,
          `• Name: ${b.name}`,
          `• Phone: ${b.phone}`,
          b.email ? `• Email: ${b.email}` : null,
          `• Date: ${b.date}`,
          `• Time: ${b.time}`,
          `• Pickup: ${b.origin}`,
          `• Drop-off: ${b.destination}`,
          !isDisposal && b.distanceKm ? `• Distance: ${b.distanceKm} km · ~${b.durationMin} min` : null,
          !isDisposal ? `• Zone: ${b.zone} — €${b.price}` : null,
          isDisposal ? `• Block: ${b.disposalBlock}${Number(b.extraHours) > 0 ? ` + ${b.extraHours}h extra` : ""}` : null,
          isDisposal ? `• Total: €${b.price}` : null,
          `• Passengers: ${b.passengers}`,
          `• Vehicle: Mercedes Vito (8 seats) · VIP`,
          b.notes ? `• Notes: ${b.notes}` : null,
          "",
          "Thanks!",
        ];
    return encodeURIComponent(lines.filter(Boolean).join("\n"));
  };

  const buildWhatsappUrl = (booking) => `${WHATSAPP_SEND_URL}&text=${buildWhatsappMessage(booking)}`;

  const openWhatsappWith = (booking) => {
    window.open(buildWhatsappUrl(booking), "_blank", "noopener,noreferrer");
  };

  const onNewQuote = (e) => {
    if (e) e.preventDefault();
    setForm({
      name: "",
      phone: "",
      email: "",
      date: "",
      time: "",
      origin: "",
      destination: "",
      zone: "",
      passengers: 2,
      notes: "",
      serviceType: "transfer",
      disposalBlock: "3h",
      extraHours: 0,
    });
    setRouteInfo(null);
    setLastBooking(null);
    toast.success(lang === "es" ? "Nuevo presupuesto listo" : "New quote ready");
  };

  // Pre-computed WhatsApp URL ready when the form is complete — used as a real
  // <a href> so Safari/iOS don't block the popup after an async await.
  const whatsappHref = useMemo(() => {
    const baseOk = form.name && form.phone && form.date && form.time && form.origin && form.destination;
    if (!baseOk) return null;
    if (form.serviceType === "transfer" && !form.zone) return null;
    if (form.serviceType === "disposal" && !form.disposalBlock) return null;
    const booking = {
      ...form,
      price: price || routeInfo?.price || 0,
      distanceKm: routeInfo?.distanceKm,
      durationMin: routeInfo?.durationMin,
    };
    return buildWhatsappUrl(booking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, price, routeInfo, lang]);

  const onConfirmWhatsapp = (e) => {
    if (!whatsappHref) {
      if (e) e.preventDefault();
      toast.error(t.booking.required);
      return;
    }
    // Let the <a href target="_blank"> do the navigation natively (Safari-friendly).
    // Fire-and-forget: persist the booking in the background so the email/.ics go out
    // AND we have a record. If the API fails, the WhatsApp chat still opens with all data.
    const bookingLocal = {
      ...form,
      price: price || routeInfo?.price || 0,
      id: "local-" + Date.now(),
      distanceKm: routeInfo?.distanceKm,
      durationMin: routeInfo?.durationMin,
    };
    setLastBooking(bookingLocal);

    // Copy the full booking message to the clipboard so the user can paste it
    // if the WhatsApp prefill fails (e.g. iOS Safari when opened in-app).
    //
    // IMPORTANT: on mobile the <a href="wa.me/..."> navigates immediately to
    // the WhatsApp app, killing any async work started here. We MUST perform
    // the copy synchronously *inside* the click handler, and the most
    // universally supported synchronous copy path is the legacy
    // `document.execCommand("copy")` on a hidden textarea. The modern
    // `navigator.clipboard.writeText` returns a Promise and is often lost
    // when the page unloads, so we fire it as an extra insurance but do NOT
    // rely on it.
    try {
      const raw = decodeURIComponent(buildWhatsappMessage(bookingLocal));

      // 1) Synchronous legacy copy — works on virtually every browser/webview
      const ta = document.createElement("textarea");
      ta.value = raw;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "0";
      ta.style.left = "0";
      ta.style.opacity = "0";
      ta.style.pointerEvents = "none";
      document.body.appendChild(ta);
      // iOS needs a real selection range, not just .select()
      ta.focus();
      ta.setSelectionRange(0, raw.length);
      let ok = false;
      try { ok = document.execCommand("copy"); } catch (_) { ok = false; }
      document.body.removeChild(ta);

      // 2) Best-effort async copy for browsers that block the legacy API
      if (!ok && navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(raw).catch(() => {});
      }

      if (ok || (navigator.clipboard && window.isSecureContext)) {
        toast.success(
          lang === "es"
            ? "Datos copiados al portapapeles — pégalos en WhatsApp si no aparecen"
            : "Details copied to clipboard — paste in WhatsApp if not prefilled"
        );
      }
    } catch (_) { /* clipboard is a bonus, don't block */ }

    setSubmitting(true);
    const payload = {
      ...form,
      email: form.email && form.email.trim() ? form.email.trim() : undefined,
      passengers: Number(form.passengers) || 1,
      language: lang,
      service_type: form.serviceType,
      disposal_block: form.serviceType === "disposal" ? form.disposalBlock : "",
      extra_hours: form.serviceType === "disposal" ? (Number(form.extraHours) || 0) : 0,
    };
    axios
      .post(`${API}/bookings`, payload)
      .then(({ data }) => {
        setLastBooking({ ...bookingLocal, price: data.price, id: data.id });
        toast.success(t.booking.success);
      })
      .catch((err) => {
        console.error("Booking API failed (WhatsApp chat still opened):", err);
        toast.warning(
          lang === "es"
            ? "Abrimos WhatsApp con tus datos, pero no se pudo guardar la reserva. Envíanos el mensaje y la procesamos."
            : "We opened WhatsApp with your data, but could not save the booking. Send us the message and we'll process it."
        );
      })
      .finally(() => setSubmitting(false));
    // Do NOT preventDefault — let the <a> open the WhatsApp URL natively
  };

  return (
    <div className="min-h-screen bg-[#070B19] text-white selection:bg-[#C9A961]/30 selection:text-white">
      <Toaster theme="dark" position="top-center" richColors />

      {/* ================= HEADER ================= */}
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "bg-[#070B19]/85 backdrop-blur-2xl border-b border-[#C9A961]/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between py-4">
          <button data-testid="logo-home-btn" onClick={() => scrollTo("top")} className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-full bg-white/95 p-1.5 ring-1 ring-[#C9A961]/30 transition-transform group-hover:scale-105">
              <img src={LOGO_CLEAN} alt="IBIALCON" className="w-full h-full object-contain" />
            </div>
            <span className="font-[Playfair_Display] text-xl tracking-wider text-white">IBIALCON</span>
          </button>

          <nav className="hidden md:flex items-center gap-10 text-[13px] uppercase tracking-[0.2em] text-white/80">
            <button data-testid="nav-booking-btn" onClick={() => scrollTo("booking")} className="hover:text-[#C9A961] transition-colors">{t.nav.booking}</button>
            <button data-testid="nav-services-btn" onClick={() => scrollTo("services")} className="hover:text-[#C9A961] transition-colors">{t.nav.services}</button>
            <button data-testid="nav-fleet-btn" onClick={() => scrollTo("fleet")} className="hover:text-[#C9A961] transition-colors">{t.nav.fleet}</button>
            <button data-testid="nav-rates-btn" onClick={() => scrollTo("rates")} className="hover:text-[#C9A961] transition-colors">{t.nav.rates}</button>
          </nav>

          <div className="flex items-center gap-3">
            <div data-testid="lang-selector" className="flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-[#C9A961]/30 rounded-full px-3 py-1.5">
              <IconGlobe className="w-3.5 h-3.5 text-[#C9A961]" />
              <button data-testid="lang-es-btn" onClick={() => setLang("es")} className={`px-1.5 ${lang === "es" ? "text-[#C9A961]" : "text-white/60"}`}>ES</button>
              <span className="text-white/30">/</span>
              <button data-testid="lang-en-btn" onClick={() => setLang("en")} className={`px-1.5 ${lang === "en" ? "text-[#C9A961]" : "text-white/60"}`}>EN</button>
            </div>
            <button
              data-testid="header-book-btn"
              onClick={() => scrollTo("booking")}
              className="hidden sm:inline-flex items-center justify-center px-5 py-2.5 uppercase tracking-[0.15em] text-[11px] font-semibold bg-[#C9A961] text-[#070B19] hover:bg-[#D4AF37] transition-all"
            >
              {t.nav.booking}
            </button>
          </div>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div
          className="absolute inset-y-0 right-0 w-full md:w-[72%] lg:w-[65%] bg-no-repeat bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070B19] from-10% via-[#070B19]/70 via-40% to-transparent to-75%" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#070B19] to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full">
          <div className="max-w-3xl" data-reveal>
            <div className="text-[11px] md:text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-6">
              {t.hero.overline}
            </div>
            <h1 className="font-[Playfair_Display] text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter mb-6">
              {t.hero.title}
            </h1>
            <p className="font-[Playfair_Display] italic text-2xl md:text-3xl text-[#E2C87A] mb-5">
              {t.hero.subtitle}
            </p>
            <p className="text-base md:text-lg text-white/80 max-w-xl mb-10 leading-relaxed">
              {t.hero.desc}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-book-btn"
                onClick={() => scrollTo("booking")}
                className="inline-flex items-center justify-center px-8 py-4 uppercase tracking-[0.15em] text-sm font-semibold bg-[#C9A961] text-[#070B19] hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all shadow-[0_8px_30px_rgba(201,169,97,0.25)]"
              >
                {lang === "es" ? "Calcula y Reserva" : "Calculate & Book"}
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#C9A961]/80 text-[10px] uppercase tracking-[0.3em] z-10">
          {t.hero.scroll}
          <IconChevron className="w-4 h-4 animate-bounce" />
        </div>
      </section>

      {/* ================= CLAUSE ================= */}
      <section data-testid="clause-section" className="relative py-24 md:py-36 bg-[#070B19]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center" data-reveal>
          <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-6">{t.clause.overline}</div>
          <p className="font-[Playfair_Display] italic text-3xl md:text-5xl leading-snug text-white mb-6">
            “{t.clause.quote}”
          </p>
          <p className="text-[#C9A961] tracking-[0.25em] uppercase text-xs md:text-sm">{t.clause.sub}</p>
        </div>
      </section>

      {/* ================= BOOKING ================= */}
      <section id="booking" data-testid="booking-section" className="relative py-24 md:py-32 bg-[#070B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7" data-reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.booking.overline}</div>
            <h2 className="font-[Playfair_Display] text-4xl md:text-6xl leading-tight mb-4">{t.booking.title}</h2>
            <p className="text-white/70 text-lg mb-10 max-w-xl">{t.booking.desc}</p>

            <form onSubmit={onNewQuote} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
              {/* Service type toggle */}
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">
                  {t.booking.serviceType.label}
                </label>
                <div className="grid grid-cols-2 gap-3" data-testid="service-type-toggle">
                  {[
                    { id: "transfer", label: t.booking.serviceType.transfer, desc: t.booking.serviceType.transferDesc },
                    { id: "disposal", label: t.booking.serviceType.disposal, desc: t.booking.serviceType.disposalDesc },
                  ].map((opt) => {
                    const active = form.serviceType === opt.id;
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        data-testid={`service-type-${opt.id}`}
                        onClick={() => setForm((p) => ({ ...p, serviceType: opt.id, zone: opt.id === "disposal" ? "" : p.zone }))}
                        className={`text-left px-5 py-4 border transition-all ${
                          active
                            ? "bg-[#C9A961]/15 border-[#C9A961] text-white"
                            : "bg-transparent border-[#C9A961]/25 text-white/70 hover:border-[#C9A961]/60"
                        }`}
                      >
                        <div className="font-[Playfair_Display] text-xl mb-1">{opt.label}</div>
                        <div className="text-xs text-white/60">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field testid="booking-name" label={t.booking.fields.name} value={form.name} onChange={handleChange("name")} required />
              <PhoneField
                label={t.booking.fields.phone}
                value={form.phone}
                onChange={(v) => setForm((p) => ({ ...p, phone: v || "" }))}
                lang={lang}
              />
              <Field testid="booking-email" label={t.booking.fields.email} value={form.email} onChange={handleChange("email")} type="email" />
              <Field testid="booking-passengers" label={t.booking.fields.passengers} value={form.passengers} onChange={handleChange("passengers")} type="number" min={1} max={8} />
              <Field testid="booking-date" label={t.booking.fields.date} value={form.date} onChange={handleChange("date")} type="date" required />
              <Field testid="booking-time" label={t.booking.fields.time} value={form.time} onChange={handleChange("time")} type="time" required />

              <div className="md:col-span-2">
                {form.serviceType === "transfer" ? (
                  <RouteMap
                    origin={form.origin}
                    destination={form.destination}
                    zones={zones}
                    onOriginChange={(v) => setForm((p) => ({ ...p, origin: v }))}
                    onDestinationChange={(v) => setForm((p) => ({ ...p, destination: v }))}
                    onRouteChange={(info) => {
                      setRouteInfo(info);
                      if (info) setForm((p) => ({ ...p, zone: info.zone }));
                    }}
                    lang={lang}
                  />
                ) : (
                  <DisposalSection
                    form={form}
                    setForm={setForm}
                    disposal={disposal}
                    quote={disposalQuote}
                    t={t.booking}
                    lang={lang}
                  />
                )}
              </div>

              <Field testid="booking-notes" label={t.booking.fields.notes} value={form.notes} onChange={handleChange("notes")} textarea className="md:col-span-2" />

              <div className="md:col-span-2 pt-2 flex flex-wrap gap-4">
                <button
                  type="submit"
                  data-testid="new-quote-btn"
                  className="inline-flex items-center justify-center px-8 py-4 uppercase tracking-[0.15em] text-sm font-semibold bg-transparent border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961]/10 hover:-translate-y-0.5 transition-all"
                >
                  {lang === "es" ? "Nuevo presupuesto" : "New quote"}
                </button>
                <button
                  type="button"
                  data-testid="continue-to-quote-btn"
                  onClick={() => {
                    const el = document.getElementById("quote-card");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="inline-flex items-center justify-center px-8 py-4 uppercase tracking-[0.15em] text-sm font-semibold bg-[#C9A961] text-[#070B19] hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all"
                >
                  {lang === "es" ? "Continuar con la reserva →" : "Continue to booking →"}
                </button>
              </div>
            </form>
          </div>

          {/* Quote Card */}
          <div className="lg:col-span-5" data-reveal>
            <div id="quote-card" className="lg:sticky lg:top-28 bg-[#0D1424]/80 backdrop-blur-xl border border-[#C9A961]/30 p-10">
              <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.booking.quoteTitle}</div>

              <div className="flex items-end gap-2 mb-8">
                <span className="font-[Playfair_Display] text-7xl md:text-8xl text-white leading-none">
                  {price ?? "--"}
                </span>
                <span className="font-[Playfair_Display] text-3xl text-[#C9A961] pb-2">€</span>
              </div>

              <div className="border-t border-[#C9A961]/20 pt-6 mb-6">
                {form.serviceType === "transfer" ? (
                  <>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] mb-2">
                      {form.zone ? t.zoneNames[form.zone] : (lang === "es" ? "Selecciona una zona" : "Select a zone")}
                    </p>
                    <p className="text-white/80 leading-relaxed">{t.booking.vehicleLine}</p>
                    {routeInfo && (
                      <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-white/70">
                        <div className="border border-[#C9A961]/15 p-3">
                          <div className="text-[9px] uppercase tracking-[0.25em] text-[#C9A961]">{lang === "es" ? "Distancia" : "Distance"}</div>
                          <div className="font-[Playfair_Display] text-lg text-white mt-1">{routeInfo.distanceKm} km</div>
                        </div>
                        <div className="border border-[#C9A961]/15 p-3">
                          <div className="text-[9px] uppercase tracking-[0.25em] text-[#C9A961]">{lang === "es" ? "Duración" : "Duration"}</div>
                          <div className="font-[Playfair_Display] text-lg text-white mt-1">~{routeInfo.durationMin} min</div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] mb-2">
                      {t.booking.serviceType.disposal} · {form.disposalBlock}
                      {Number(form.extraHours) > 0 ? ` + ${form.extraHours}h` : ""}
                    </p>
                    <p className="text-white/80 leading-relaxed">{t.booking.vehicleLine}</p>
                    {disposalQuote && disposalQuote.discountPct > 0 && (
                      <div className="mt-4 text-xs text-white/70">
                        <div className="flex items-center justify-between border border-[#C9A961]/15 p-3">
                          <span>{t.booking.disposal.before}</span>
                          <span className="line-through text-white/50">
                            {disposalQuote.basePrice + disposalQuote.extraTotal}€
                          </span>
                        </div>
                        <div className="flex items-center justify-between border border-[#C9A961]/15 p-3 mt-2 text-[#C9A961]">
                          <span>{t.booking.disposal.discount} ({disposalQuote.discountPct}%)</span>
                          <span>-{disposalQuote.discountAmount}€</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-3 text-sm text-white/70 mb-8">
                <li>· {lang === "es" ? "Chófer profesional" : "Professional chauffeur"}</li>
                <li>· {lang === "es" ? "Wi-Fi y climatización" : "Wi-Fi & climate control"}</li>
                <li>· {lang === "es" ? "Sin recargos nocturnos ocultos" : "No hidden night fees"}</li>
              </ul>

              <a
                data-testid="quote-whatsapp-btn"
                href={whatsappHref || WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onConfirmWhatsapp}
                aria-disabled={!whatsappHref}
                className={`w-full inline-flex items-center justify-center gap-3 px-8 py-4 uppercase tracking-[0.15em] text-sm font-semibold bg-[#25D366] text-white hover:bg-[#1ebe5a] transition-all ${submitting ? "opacity-70" : ""}`}
              >
                <IconWhatsapp className="w-5 h-5" />
                {submitting ? (lang === "es" ? "Enviando..." : "Sending...") : t.booking.whatsappBtn}
              </a>

              {lastBooking && (
                <div data-testid="clipboard-fallback" className="mt-5 border border-[#C9A961]/20 bg-[#070B19]/60 p-4 text-xs text-white/75 leading-relaxed">
                  <p className="text-[#C9A961] text-[10px] uppercase tracking-[0.25em] mb-2">
                    {lang === "es" ? "¿No se abrió el chat con tus datos?" : "Chat didn't open with your info?"}
                  </p>
                  <p>
                    {lang === "es"
                      ? "Algunos móviles antiguos no rellenan el mensaje automáticamente. Copia los datos y pégalos en el chat."
                      : "Some older phones don't prefill the message automatically. Copy the data and paste it in the chat."}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      type="button"
                      data-testid="copy-info-btn"
                      onClick={async () => {
                        try {
                          const raw = decodeURIComponent(buildWhatsappMessage(lastBooking));
                          await navigator.clipboard.writeText(raw);
                          toast.success(lang === "es" ? "Copiado al portapapeles" : "Copied to clipboard");
                        } catch {
                          toast.error(lang === "es" ? "No se pudo copiar" : "Could not copy");
                        }
                      }}
                      className="inline-flex items-center justify-center px-4 py-2 uppercase tracking-[0.2em] text-[10px] bg-[#C9A961] text-[#070B19] hover:bg-[#D4AF37]"
                    >
                      {lang === "es" ? "Copiar datos" : "Copy data"}
                    </button>
                    <button
                      type="button"
                      data-testid="reopen-whatsapp-btn"
                      onClick={() => openWhatsappWith(lastBooking)}
                      className="inline-flex items-center justify-center px-4 py-2 uppercase tracking-[0.2em] text-[10px] border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
                    >
                      {lang === "es" ? "Abrir chat otra vez" : "Reopen chat"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-[#0A1020] border-t border-[#C9A961]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6" data-reveal>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.services.overline}</div>
              <h2 className="font-[Playfair_Display] text-4xl md:text-6xl leading-tight">{t.services.title}</h2>
            </div>
            <div className="h-px flex-1 bg-[#C9A961]/20 hidden md:block ml-12" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {t.services.items.map((it, i) => (
              <div
                key={i}
                data-testid={`service-card-${i}`}
                data-reveal
                className="group relative bg-[#0D1424] border border-[#C9A961]/15 p-10 md:p-12 hover:border-[#C9A961]/50 transition-all duration-500"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="text-[#C9A961] font-[Playfair_Display] text-5xl mb-8">0{i + 1}</div>
                <h3 className="font-[Playfair_Display] text-2xl md:text-3xl mb-4">{it.title}</h3>
                <p className="text-white/70 leading-relaxed">{it.desc}</p>
                <div className="mt-8 h-px w-12 bg-[#C9A961] group-hover:w-24 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FLEET ================= */}
      <section id="fleet" data-testid="fleet-section" className="relative py-24 md:py-32 bg-[#070B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative h-[560px]" data-reveal>
            <div className="absolute top-0 left-0 w-[78%] h-[68%] overflow-hidden border border-[#C9A961]/20 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
              <img src={VITO_EXT_NIGHT} alt="Mercedes Vito exterior" className="w-full h-full object-cover" />
            </div>
            <div className="absolute bottom-0 right-0 w-[62%] h-[58%] overflow-hidden border border-[#C9A961]/30 shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
              <img src={VITO_INTERIOR_DUSK} alt="Mercedes Vito interior" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-[40%] right-[-8%] hidden lg:flex w-36 h-36 rounded-full border border-[#C9A961]/40 backdrop-blur-sm bg-[#070B19]/60 items-center justify-center">
              <span className="font-[Playfair_Display] italic text-[#C9A961] text-lg">8 plazas</span>
            </div>
          </div>

          <div className="lg:col-span-6" data-reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.fleet.overline}</div>
            <h2 className="font-[Playfair_Display] text-4xl md:text-6xl leading-tight mb-10">{t.fleet.title}</h2>
            <ul className="space-y-8">
              {t.fleet.features.map((f, i) => (
                <li key={i} data-testid={`fleet-feature-${i}`} className="flex gap-6 border-b border-[#C9A961]/10 pb-6">
                  <div className="font-[Playfair_Display] text-[#C9A961] text-3xl">·{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <h4 className="font-[Playfair_Display] text-xl md:text-2xl mb-2">{f.title}</h4>
                    <p className="text-white/70 leading-relaxed">{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Fleet gallery strip — real photos */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20 grid grid-cols-1 md:grid-cols-2 gap-6" data-reveal>
          <div className="group relative aspect-[4/3] overflow-hidden border border-[#C9A961]/20">
            <img src={VITO_FLEET_AIRPORT} alt="Flota IBIALCON en aeropuerto AviaVIP" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#070B19] via-[#070B19]/70 to-transparent">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">AviaVIP · Sky Valet</p>
              <p className="font-[Playfair_Display] text-xl text-white">Flota privada en aeropuerto</p>
            </div>
          </div>
          <div className="group relative aspect-[4/3] overflow-hidden border border-[#C9A961]/20">
            <img src={VITO_HOTEL_NIGHT} alt="Mercedes Vito IBIROAD en hotel" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#070B19] via-[#070B19]/70 to-transparent">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Traslados a hoteles</p>
              <p className="font-[Playfair_Display] text-xl text-white">Servicio nocturno VIP</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= RATES ================= */}
      <section id="rates" data-testid="rates-section" className="relative py-24 md:py-32 bg-[#0A1020] border-t border-[#C9A961]/10">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-14 max-w-2xl" data-reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.rates.overline}</div>
            <h2 className="font-[Playfair_Display] text-4xl md:text-6xl leading-tight mb-4">{t.rates.title}</h2>
            <p className="text-white/70 text-lg">{t.rates.desc}</p>
          </div>

          <div className="border border-[#C9A961]/20 divide-y divide-[#C9A961]/10 bg-[#0D1424]/60 backdrop-blur-sm">
            <div className="grid grid-cols-12 px-6 md:px-10 py-5 text-[11px] uppercase tracking-[0.25em] text-[#C9A961]">
              <div className="col-span-2 md:col-span-2">{t.rates.headers.zone}</div>
              <div className="col-span-7 md:col-span-8">{t.rates.headers.desc}</div>
              <div className="col-span-3 md:col-span-2 text-right">{t.rates.headers.price}</div>
            </div>
            {zones.map((z) => (
              <div
                key={z.id}
                data-testid={`rate-row-${z.id}`}
                className="grid grid-cols-12 px-6 md:px-10 py-6 md:py-8 items-center hover:bg-[#C9A961]/5 transition-colors cursor-pointer"
                onClick={() => { setForm((p) => ({ ...p, serviceType: "transfer", zone: z.id })); scrollTo("booking"); }}
              >
                <div className="col-span-2 md:col-span-2 font-[Playfair_Display] text-3xl md:text-5xl text-[#C9A961]">
                  {z.id}
                </div>
                <div className="col-span-7 md:col-span-8 text-white/80">
                  {t.zoneNames[z.id]}
                </div>
                <div className="col-span-3 md:col-span-2 text-right">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 block">{t.rates.from}</span>
                  <span className="font-[Playfair_Display] text-3xl md:text-4xl">{z.price}€</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= RINCON ================= */}
      <section
        id="rincon"
        data-testid="rincon-section"
        className="relative min-h-[56vh] md:min-h-[80vh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: `url(${ES_VEDRA})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070B19]/90 via-[#070B19]/50 to-[#070B19]/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full py-24">
          <div className="max-w-xl bg-[#070B19]/70 backdrop-blur-xl border border-[#C9A961]/30 p-10 md:p-12" data-reveal>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.rincon.overline}</div>
            <h2 className="font-[Playfair_Display] text-3xl md:text-5xl leading-tight mb-6">{t.rincon.title}</h2>
            <p className="text-white/80 leading-relaxed">{t.rincon.desc}</p>
          </div>
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      <Reviews lang={lang} />

      {/* ================= FOOTER ================= */}
      <footer id="contact" data-testid="site-footer" className="relative bg-[#050813] border-t border-[#C9A961]/10 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-white/95 p-1.5 ring-1 ring-[#C9A961]/30">
                <img src={LOGO_CLEAN} alt="IBIALCON" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-[Playfair_Display] text-xl tracking-wider">IBIALCON</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{t.footer.tagline}</div>
              </div>
            </div>
            <p className="text-white/60 text-sm max-w-xs">{t.footer.vip}</p>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-4">{t.footer.contact}</h4>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="inline-flex items-center gap-3 text-white/80 hover:text-[#C9A961] transition-colors"
            >
              <IconWhatsapp className="w-5 h-5 text-[#25D366]" />
              {t.footer.whatsapp}
            </a>
          </div>
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] mb-4">Ibiza</h4>
            <p className="text-white/60 text-sm leading-relaxed">
              Servicio VIP 24h · Aeropuerto, villas, eventos y traslados privados por toda la isla.
            </p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-12 pt-6 border-t border-[#C9A961]/10 text-[11px] uppercase tracking-[0.3em] text-white/40 flex flex-col md:flex-row gap-3 justify-between items-center">
          <span>© {new Date().getFullYear()} IBIALCON</span>
          <a
            href="/admin/login"
            data-testid="admin-access-link"
            className="text-white/40 hover:text-[#C9A961] transition-colors"
          >🔒 Acceso gerente</a>
          <span>{t.footer.rights}</span>
        </div>
      </footer>

      {/* ================= WHATSAPP FLOATING ================= */}
      <a
        href={whatsappHref || WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={whatsappHref ? onConfirmWhatsapp : undefined}
        data-testid="whatsapp-float-btn"
        aria-label="WhatsApp"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-[0_8px_30px_rgba(37,211,102,0.45)] hover:scale-110 active:scale-95 transition-transform"
      >
        <IconWhatsapp className="w-7 h-7" />
      </a>

      {/* ================= RADIO PLAYER (floating, always visible) ================= */}
      <RadioPlayer lang={lang} />
    </div>
  );
}

function DisposalSection({ form, setForm, disposal, quote, t, lang }) {
  const blocks = ["3h", "6h", "12h", "24h"];
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">
          {t.disposal.blockLabel} *
        </label>
        <div className="grid grid-cols-4 gap-2" data-testid="disposal-block-picker">
          {blocks.map((b) => {
            const cfg = disposal?.[b] || {};
            const price = Number(cfg.price || 0);
            const pct = Number(cfg.discount_pct || 0);
            const finalPrice = Math.round(price * (1 - pct / 100));
            const active = form.disposalBlock === b;
            return (
              <button
                type="button"
                key={b}
                data-testid={`disposal-block-${b}`}
                onClick={() => setForm((p) => ({ ...p, disposalBlock: b }))}
                className={`px-3 py-4 border text-center transition-all ${
                  active
                    ? "bg-[#C9A961]/15 border-[#C9A961] text-white"
                    : "bg-transparent border-[#C9A961]/25 text-white/70 hover:border-[#C9A961]/60"
                }`}
              >
                <div className="font-[Playfair_Display] text-2xl">{b}</div>
                <div className="mt-1 text-xs">
                  {pct > 0 && (
                    <div className="text-white/40 line-through text-[10px]">{price}€</div>
                  )}
                  <div className={`text-[13px] ${active ? "text-[#C9A961]" : "text-white/60"}`}>{finalPrice}€</div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-white/45 mt-2">{t.disposal.blockHint}</p>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">
          {t.disposal.extraHours}
        </label>
        <input
          type="number"
          min={0}
          max={24}
          value={form.extraHours}
          onChange={(e) => setForm((p) => ({ ...p, extraHours: Math.max(0, Number(e.target.value) || 0) }))}
          data-testid="disposal-extra-hours"
          className="w-32 bg-transparent border-b border-[#C9A961]/30 focus:border-[#C9A961] text-white py-2 outline-none transition-colors"
        />
        <p className="text-[11px] text-white/45 mt-2">
          {disposal?.extra_hour || 70}€ / {lang === "es" ? "hora" : "hour"}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FieldRow
          label={lang === "es" ? "Origen / Punto de recogida" : "Pickup point"}
          value={form.origin}
          onChange={(v) => setForm((p) => ({ ...p, origin: v }))}
          testid="disposal-origin-input"
          placeholder={lang === "es" ? "Hotel, villa, dirección..." : "Hotel, villa, address..."}
        />
        <FieldRow
          label={lang === "es" ? "Zona / Destino estimado" : "Approx. drop-off zone"}
          value={form.destination}
          onChange={(v) => setForm((p) => ({ ...p, destination: v }))}
          testid="disposal-destination-input"
          placeholder={lang === "es" ? "Ej. Por toda la isla" : "e.g. Around the island"}
        />
      </div>

      {quote && (
        <div data-testid="disposal-quote-info" className="grid grid-cols-3 border border-[#C9A961]/30 bg-[#0D1424]/80 backdrop-blur-sm">
          <div className="p-4 border-r border-[#C9A961]/15">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">{form.disposalBlock}</div>
            <div className="font-[Playfair_Display] text-xl mt-1">{quote.blockDiscounted}€</div>
          </div>
          <div className="p-4 border-r border-[#C9A961]/15">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">
              + {form.extraHours || 0}h {lang === "es" ? "extra" : "extra"}
            </div>
            <div className="font-[Playfair_Display] text-xl mt-1">{quote.extraTotal}€</div>
          </div>
          <div className="p-4 bg-[#C9A961]/10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A961]">{t.disposal.total}</div>
            <div className="font-[Playfair_Display] text-xl mt-1 text-[#C9A961]">{quote.total}€</div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-2">{label} *</label>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#C9A961]/30 focus:border-[#C9A961] text-white py-2 outline-none transition-colors placeholder:text-white/30"
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "", textarea = false, testid, min, max }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">{label}{required ? " *" : ""}</label>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={onChange}
          rows={3}
          className="w-full bg-transparent border-b border-[#C9A961]/30 focus:border-[#C9A961] text-white py-2 outline-none transition-colors resize-none"
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          min={min}
          max={max}
          className="w-full bg-transparent border-b border-[#C9A961]/30 focus:border-[#C9A961] text-white py-2 outline-none transition-colors"
        />
      )}
    </div>
  );
}

const PHONE_COUNTRIES = [
  "ES", "GB", "FR", "DE", "IT", "NL", "BE", "CH", "IE", "PT",
  "AT", "SE", "NO", "DK", "FI", "US", "CA", "AU", "BR", "IN",
];

function PhoneField({ label, value, onChange, lang }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-3">{label} *</label>
      <PhoneInput
        data-testid="booking-phone"
        international
        defaultCountry="ES"
        countries={PHONE_COUNTRIES}
        addInternationalOption={false}
        value={value}
        onChange={onChange}
        placeholder={lang === "es" ? "Ej. 612 345 678" : "e.g. 612 345 678"}
        className="ibialcon-phone-input"
      />
    </div>
  );
}

